import { AdmissionInfo } from '@informme/shared/schemas/admission/AdmissionModel';
import {
  PatientScansQRCodeResponse,
  PatientSessionInfo,
} from '@informme/shared/schemas/bunduril/patient-ui/patient-scans-qr-code.schema';
import {
  V2_FormMinimumData as FormMinimumData,
  FormRevision,
} from '@informme/shared/schemas/form/FormRevision';

export const TEST_QR_ID = '12345678-1234-4234-a234-123456789abc';

export const TEST_ORG_ID = 'test-org-001';

export const TEST_ADMISSION_INFO: AdmissionInfo = {
  risSiteId: 'test-site-001',
  admissionId: 'test-admission-001',
  patientId: 'test-patient-001',
  caseId: 'test-case-001',
  firstName: 'Test',
  lastName: 'Patient',
  birthdate: '1990-01-01',
  phoneNumber: '+1234567890',
  mobileNumber: '+9876543210',
  email: 'test.patient@example.com',
  homeAddress: '123 Test Street, Test City',
  insuranceType: 'K',
  gender: 'M',
  plannedMedicalProcedures: ['Test Procedure'],
  admissionTime: '2024-01-01T10:00:00Z',
  siteId: 'test-site-001',
  matchedFFPProcessIds: [],
  assignedFormIds: ['form-demo-20'],
  qrCodeUrl: `http://localhost:8181/#/language?qrid=${TEST_QR_ID}`,
  patientSessionId: 'test-session-12345678-1234-1234-1234-123456789abc',
};

export const TEST_PATIENT_SESSION_INFO: PatientSessionInfo = {
  siteId: TEST_ADMISSION_INFO.siteId,
  assignedFormIds: TEST_ADMISSION_INFO.assignedFormIds,
  patientSessionId: 'test-session-12345678-1234-1234-1234-123456789abc',
  orgId: TEST_ORG_ID,
  qas: [
    {
      id: 'first_name_question',
      answer: TEST_ADMISSION_INFO.firstName,
    },
    {
      id: 'last_name_question',
      answer: TEST_ADMISSION_INFO.lastName,
    },
    {
      id: 'birthdate_question',
      answer: TEST_ADMISSION_INFO.birthdate,
    },
    {
      id: 'phone_number_question',
      answer: TEST_ADMISSION_INFO.phoneNumber || '',
    },
    {
      id: 'mobile_number_question',
      answer: TEST_ADMISSION_INFO.mobileNumber || '',
    },
    {
      id: 'email_question',
      answer: TEST_ADMISSION_INFO.email || '',
    },
  ],
  formStatuses: [],
};

export const TEST_FORM_MIN_DATA: FormMinimumData[] = [
  {
    id: 'form-demo-20',
    formName: 'Demo Form 20',
    label: 'Demo Form 20',
    lang: 'en',
    visible: true,
    preSelected: false,
    image: 'forms/demo-form.webp',
    category: 'demo',
  },
];

export const TEST_FORM_REVISION: FormRevision = {
  id: 'form-demo-20',
  formName: 'Demo Form 20',
  label: 'Demo Form 20',
  lang: 'en',
  visible: true,
  preSelected: false,
  image: 'forms/demo-form.webp',
  category: 'demo',
  pages: [
    {
      id: 'test-textfield',
      type: 'qa_textfield',
      title: 'Text Input Test',
      description: 'Test page for text field input',
      content: {
        text: 'What is your name?',
        print_text: 'What is your name?',
        detailsText: 'Please enter your full name',
        fileName: '',
      },
      tags: [],
    },
    {
      id: 'test-textarea',
      type: 'qa_textarea',
      title: 'Textarea Test',
      description: 'Test page for textarea input',
      content: {
        text: 'Please describe your symptoms in detail',
        print_text: 'Please describe your symptoms in detail',
        detailsText: 'Provide as much detail as possible',
        fileName: '',
      },
      tags: [],
    },
    {
      id: 'test-yes-no',
      type: 'qa_yes_no',
      title: 'Yes/No Test',
      description: 'Test page for yes/no questions',
      content: {
        text: 'Do you have any allergies?',
        print_text: 'Do you have any allergies?',
        detailsText: 'Select yes or no',
        fileName: '',
      },
      tags: [],
    },
    {
      id: 'test-select',
      type: 'qa_select',
      title: 'Select Test',
      description: 'Test page for single select dropdown',
      content: {
        text: 'What is your preferred contact method?',
        print_text: 'What is your preferred contact method?',
        detailsText: 'Choose one option',
        fileName: '',
        qaOptions: [
          { option: 'Email', printOption: 'Email' },
          { option: 'Phone', printOption: 'Phone' },
          { option: 'SMS', printOption: 'SMS' },
        ],
      },
      tags: [],
    },
    {
      id: 'test-multiple-select',
      type: 'qa_multiple_select',
      title: 'Multiple Select Test',
      description: 'Test page for multiple select checkboxes',
      content: {
        text: 'Which symptoms are you experiencing?',
        print_text: 'Which symptoms are you experiencing?',
        detailsText: 'Select all that apply',
        fileName: '',
        qaOptions: [
          { option: 'Headache', printOption: 'Headache' },
          { option: 'Fever', printOption: 'Fever' },
          { option: 'Nausea', printOption: 'Nausea' },
          { option: 'Fatigue', printOption: 'Fatigue' },
        ],
      },
      tags: [],
    },
    {
      id: 'test-number',
      type: 'qa_number',
      title: 'Number Test',
      description: 'Test page for number input',
      content: {
        text: 'What is your age?',
        print_text: 'What is your age?',
        detailsText: 'Enter your age in years',
        fileName: '',
      },
      tags: [],
    },
    {
      id: 'test-phone-number',
      type: 'qa_phone_number',
      title: 'Phone Number Test',
      description: 'Test page for phone number input',
      content: {
        text: 'What is your phone number?',
        print_text: 'What is your phone number?',
        detailsText: 'Include country code if applicable',
        fileName: '',
      },
      tags: [],
    },
    {
      id: 'test-email',
      type: 'qa_email',
      title: 'Email Test',
      description: 'Test page for email input',
      content: {
        text: 'What is your email address?',
        print_text: 'What is your email address?',
        detailsText: 'Enter a valid email address',
        fileName: '',
      },
      tags: [],
    },
    {
      id: 'test-birthdate',
      type: 'qa_birthdate',
      title: 'Birthdate Test',
      description: 'Test page for birthdate input',
      content: {
        text: 'What is your date of birth?',
        print_text: 'What is your date of birth?',
        detailsText: 'Select your birthdate',
        fileName: '',
      },
      tags: [],
    },
    {
      id: 'test-picture-select',
      type: 'qa_picture_select',
      title: 'Picture Select Test',
      description: 'Test page for picture selection',
      content: {
        text: 'Which body part is affected?',
        print_text: 'Which body part is affected?',
        detailsText: 'Click on the affected area',
        fileName: '',
        qaOptions: [
          { option: 'Head', printOption: 'Head' },
          { option: 'Chest', printOption: 'Chest' },
          { option: 'Abdomen', printOption: 'Abdomen' },
        ],
      },
      tags: [],
    },
    {
      id: 'test-picture-multiple-select',
      type: 'qa_picture_multiple_select',
      title: 'Picture Multiple Select Test',
      description: 'Test page for multiple picture selection',
      content: {
        text: 'Which areas have you experienced pain?',
        print_text: 'Which areas have you experienced pain?',
        detailsText: 'Select all affected areas',
        fileName: '',
        qaOptions: [
          { option: 'Head', printOption: 'Head' },
          { option: 'Neck', printOption: 'Neck' },
          { option: 'Back', printOption: 'Back' },
          { option: 'Legs', printOption: 'Legs' },
        ],
      },
      tags: [],
    },
    {
      id: 'test-slider',
      type: 'qa_slider',
      title: 'Slider Test',
      description: 'Test page for slider input',
      content: {
        text: 'Rate your pain level (0-10)',
        print_text: 'Rate your pain level (0-10)',
        detailsText: 'Move the slider to indicate your pain level',
        fileName: '',
        qaOptions: [
          { option: '0 - No pain', printOption: '0 - No pain' },
          { option: '10 - Severe pain', printOption: '10 - Severe pain' },
        ],
      },
      tags: [],
    },
    {
      id: 'test-drawing',
      type: 'qa_drawing',
      title: 'Drawing Test',
      description: 'Test page for drawing canvas',
      content: {
        text: 'Please draw or mark the affected area',
        print_text: 'Please draw or mark the affected area',
        detailsText: 'Use your finger or stylus to draw',
        fileName: '',
      },
      tags: [],
    },
    {
      id: 'test-photo-upload',
      type: 'qa_photo_upload',
      title: 'Photo Upload Test',
      description: 'Test page for photo upload',
      content: {
        title: 'Bitte laden sie ihren Überweisungsschein hoch',
        text: 'Sie können mehrere Fotos hinzufügen. Diese Seite ist optional.',
        print_text: 'Patient photos for referral / medication plan',
      },
      tags: ['not_mandatory'],
    },
    {
      id: 'test-submit',
      type: 'submit',
      title: 'Submit Form',
      description: 'Final page to submit the completed form',
      content: {
        text: 'Thank you for completing the form. Please review your answers and submit.',
        print_text: 'Thank you for completing the form. Please review your answers and submit.',
        detailsText: 'Click submit to send your responses',
        fileName: '',
      },
      tags: [],
    },
  ],
};

export const TEST_QR_SCAN_RESPONSE: PatientScansQRCodeResponse = {
  patientSessionInfo: TEST_PATIENT_SESSION_INFO,
};
